import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { LogIn, UserPlus, Loader2, Eye, EyeOff, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl ${className}`}>
    {children}
  </div>
);

const SimpleInput = ({ value, onChange, type = 'text', className = '', ...props }) => (
  <input
    type={type}
    value={value}
    onChange={onChange}
    className={`flex h-10 w-full rounded-md border border-white/20 bg-black/50 px-3 py-2 text-base text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${className}`}
    autoComplete="off"
    spellCheck="false"
    {...props}
  />
);

const Login = () => {
  const { theme } = useTheme();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationLocked, setRegistrationLocked] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [invitationCode, setInvitationCode] = useState('');

  useEffect(() => {
    axios.get(`${API}/auth/registration-status`).then(res => {
      setRegistrationLocked(res.data.locked);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        await register(username, email, password, registrationLocked ? invitationCode : undefined);
        toast.success('Account created successfully!');
      } else {
        await login(email, password);
        toast.success('Welcome back!');
      }
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-4 flex items-center justify-center min-h-[calc(100vh-200px)]">
      <div className="w-full max-w-md">
        <GlassCard className="p-6 sm:p-8">
          <div className="text-center mb-6">
            <h1 
              className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1"
              style={{ color: theme.primary, textShadow: theme.glow }}
              data-testid="auth-title"
            >
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h1>
            <p className="text-gray-400">
              {isRegister ? 'Join the BeatLab52 community' : 'Sign in to your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Username</label>
                <SimpleInput
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                  required={isRegister}
                  minLength={3}
                  data-testid="username-input"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Email</label>
              <SimpleInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-gray-300">Password</label>
              <div className="relative">
                <SimpleInput
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  minLength={6}
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {isRegister && registrationLocked && (
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">
                  <Ticket className="w-4 h-4 inline mr-1" />
                  Invitation Code
                </label>
                <SimpleInput
                  value={invitationCode}
                  onChange={(e) => setInvitationCode(e.target.value)}
                  placeholder="Enter your invitation code"
                  required
                  data-testid="invitation-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">Registration is invite-only. You need a code to sign up.</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full font-bold uppercase tracking-wider mt-6"
              style={{ backgroundColor: theme.primary, color: '#000' }}
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isRegister ? (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Account
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm">
              {isRegister ? 'Already have an account?' : "Don't have an account?"}{' '}
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="font-bold hover:underline"
                style={{ color: theme.primary }}
                type="button"
                data-testid="toggle-auth-btn"
              >
                {isRegister ? 'Sign In' : 'Create Account'}
              </button>
            </p>
          </div>

          <div className="mt-4 text-center">
            <Link 
              to="/" 
              className="text-sm text-gray-500 hover:text-gray-300"
            >
              ← Back to Home
            </Link>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default Login;
